import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { ReactTyped } from "react-typed";
import { motion, AnimatePresence } from "framer-motion";
import { Send, LogIn, X, ChevronLeft } from "lucide-react";
import { useWallet } from "@solana/wallet-adapter-react";
import { askAI } from "./ai.js";
import TesseractMode from "./TesseractMode.js";
import { WalletModal } from "./WalletModal.js";
import "./App.css";

// ----------------------------------------------------------------
// Helpers: Mobil cihaz & Phantom tespiti
// ----------------------------------------------------------------
const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const isAndroid = () => /Android/i.test(navigator.userAgent || '');
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent || '');

// Phantom'ın kendi in-app tarayıcısında mıyız?
const isInPhantomBrowser = () =>
  typeof window !== 'undefined' &&
  !!(window.solana?.isPhantom || window.phantom?.solana?.isPhantom);

// Backend API URL (lokal veya Vercel)
const API_BASE = process.env.NODE_ENV === 'production' ? '' : '';

// Solana nonce challenge al
async function fetchNonce(walletAddress) {
  const res = await fetch(`${API_BASE}/api/auth/challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ walletAddress }),
  });
  if (!res.ok) throw new Error('Challenge failed');
  const data = await res.json();
  return data.nonce;
}

// İmzayı backend'e doğrulat, JWT al
async function verifySignature(walletAddress, signature, nonce) {
  const res = await fetch(`${API_BASE}/api/auth/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ walletAddress, signature: Array.from(signature), nonce }),
  });
  if (!res.ok) throw new Error('Verification failed');
  return res.json(); // { token, walletAddress }
}

// Sohbet geçmişini çek
async function fetchHistory(token) {
  const res = await fetch(`${API_BASE}/api/history`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return [];
  const data = await res.json();
  return data.messages || [];
}

// Mesaj çiftini kaydet
async function saveHistoryPair(token, userMessage, botMessage) {
  try {
    await fetch(`${API_BASE}/api/history`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ userMessage, botMessage }),
    });
  } catch (err) {
    console.warn('History save failed:', err);
  }
}

// İmza için mesaj formatı (backend ile aynı olmalı)
function buildSignMessage(nonce) {
  return `MaykutoGPT — On-Chain Memory Authentication\n\nNonce: ${nonce}\n\nThis request will not trigger a blockchain transaction or cost any gas fees.`;
}

// --- Helper: Bold + newline formatter (dark red accent like TARS but crimson) ---
const formatMessage = (text) => {
  if (!text) return null;
  return text.split('\n').map((line, i) => (
    <span key={i} className="block min-h-[1.2em]">
      {line.split(/(\*\*.*?\*\*)/).map((part, j) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={j} style={{ color: '#c0392b', fontWeight: '700' }}>{part.slice(2, -2)}</strong>;
        }
        return part;
      })}
    </span>
  ));
};

// --- Thinking Indicator (inline, premium) ---
const ThinkingIndicator = () => {
  const [dots, setDots] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="font-mono text-[13px] text-white/40 tracking-wider lowercase">
      düşünüyorum{".".repeat(dots)}
    </span>
  );
};

// --- Glass Header ---
const GlassHeader = ({ isGlitching, onLoginClick, walletAddress }) => {
  // Cüzdan adresini kısalt: ilk 4 + son 4 karakter
  const shortAddr = walletAddress
    ? `${walletAddress.slice(0, 4)}...${walletAddress.slice(-4)}`
    : null;

  return (
    <motion.header
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
      className="w-full h-16 pt-[env(safe-area-inset-top)] flex items-center justify-between px-6 relative z-50"
      style={{
        background: 'rgba(0,0,0,0.2)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        animation: isGlitching ? "glitch-fade-out 2s forwards" : undefined,
      }}
    >
      {/* Left spacer */}
      <div className="w-10" />

      {/* Center: MaykutoGPT title */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
        <span
          className="text-lg tracking-[0.2em] font-light"
          style={{
            fontFamily: '"Courier New", monospace',
            color: 'rgba(255,255,255,0.92)',
            textShadow: '0 0 12px rgba(255,255,255,0.15)',
          }}
        >
          <ReactTyped
            strings={["MaykutoGPT"]}
            typeSpeed={60}
            backSpeed={30}
            loop={false}
          />
        </span>
      </div>

      {/* Right: LogIn button (Always simple, whether connected or not) */}
      <button
        onClick={onLoginClick}
        className="p-2 rounded-full transition-colors"
        style={{ color: 'rgba(255,255,255,0.6)' }}
        onMouseEnter={e => e.currentTarget.style.color = 'rgba(192,57,43,0.9)'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
      >
        <LogIn size={22} strokeWidth={1.5} />
      </button>
    </motion.header>
  );
};

// --- Message Bubble ---
const MessageBubble = ({ message }) => {
  const isBot = message.sender === "bot";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex w-full mb-6 ${isBot ? 'justify-start' : 'justify-end'}`}
    >
      <div
        className={`
          relative px-6 py-4 text-[15px] leading-relaxed tracking-wide whitespace-pre-wrap
          max-w-[85%] sm:max-w-[75%] md:max-w-[65%]
        `}
        style={isBot ? {
          borderRadius: '1.5rem 1.5rem 1.5rem 0.25rem',
          background: 'rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.05)',
          color: '#e8e8e8',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        } : {
          borderRadius: '1.5rem 1.5rem 0.25rem 1.5rem',
          background: 'rgba(255,255,255,0.06)',
          border: '1px solid rgba(255,255,255,0.05)',
          color: '#ffffff',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
      >
        <div className="font-light opacity-90">
          {formatMessage(message.text)}
        </div>
        <div
          className={`mt-2 text-[10px] opacity-30 font-mono ${isBot ? 'text-left' : 'text-right'}`}
        >
          {new Date(message.timestamp || Date.now()).toLocaleTimeString('tr-TR', {
            hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Istanbul'
          })}
        </div>
      </div>
    </motion.div>
  );
};

// --- Cryptographic Web3 Modal (Gerçek Solana Akışı) ---
const AuthModal = ({ isOpen, onClose, onAuthSuccess, walletAddress }) => {
  const { select, wallet, connect, disconnect, connected, publicKey, signMessage } = useWallet();

  // step: 'idle' | 'mobile-redirect' | 'connecting' | 'signing' | 'verifying' | 'success' | 'error'
  const [step, setStep] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedWalletName, setSelectedWalletName] = useState('CÜZDAN');
  const [showWalletSelector, setShowWalletSelector] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const mobile = isMobileDevice();
  const inPhantom = isInPhantomBrowser();

  useEffect(() => {
    if (!isOpen) {
      setStep('idle');
      setErrorMsg('');
      setShowWalletSelector(false);
      setSelectedWalletName('CÜZDAN');
      setShowDetails(false);
    }
  }, [isOpen]);

  // Eğer cüzdan zaten DApp'e bağlı ise ve kullanıcı oturum açmamışsa, modal açıldığında otomatik olarak imza aşamasına (connecting) geç
  useEffect(() => {
    if (isOpen && connected && publicKey && step === 'idle' && !walletAddress) {
      console.log("[AuthModal] Cüzdan zaten bağlı. İmzalama akışı otomatik tetikleniyor...");
      setStep('connecting');
      if (wallet?.adapter?.name) {
        setSelectedWalletName(wallet.adapter.name);
      }
    }
  }, [isOpen, connected, publicKey, step, wallet, walletAddress]);

  // Cüzdan seçildiğinde otomatik bağlantı isteği aç
  useEffect(() => {
    if (wallet && !connected && step === 'connecting') {
      const doConnect = async () => {
        try {
          await connect();
        } catch (err) {
          console.error('Wallet connect error:', err);
          setErrorMsg('Cüzdan bağlantısı veya imza isteği kullanıcı tarafından reddedildi.');
          setStep('error');
        }
      };
      doConnect();
    }
  }, [wallet, connected, step, connect]);

  // Bağlantı kurulduğunda otomatik olarak imzalama adımına geç
  useEffect(() => {
    if (connected && publicKey && step === 'connecting') {
      handleSign();
    }
  }, [connected, publicKey, step]);

  const handleSign = async () => {
    if (!publicKey || !signMessage) return;
    setStep('signing');
    try {
      const address = publicKey.toBase58();
      // 1. Nonce al
      const nonce = await fetchNonce(address);

      // 2. Mesajı encode et ve imzala
      const message = buildSignMessage(nonce);
      const encodedMessage = new TextEncoder().encode(message);
      const signature = await signMessage(encodedMessage);

      // 3. Backend'de doğrula
      setStep('verifying');
      const { token } = await verifySignature(address, signature, nonce);

      // 4. JWT'yi kaydet ve başarı
      localStorage.setItem('mgpt_token', token);
      localStorage.setItem('mgpt_wallet', address);
      setStep('success');
      onAuthSuccess(token, address);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      console.error('Auth error:', err);
      setErrorMsg('Cüzdan bağlantısı veya imza isteği kullanıcı tarafından reddedildi.');
      setStep('error');
    }
  };

  const handleDisconnect = async () => {
    await disconnect();
    localStorage.removeItem('mgpt_token');
    localStorage.removeItem('mgpt_wallet');
    onAuthSuccess(null, null);
    onClose();
  };

  const progressWidth =
    step === 'connecting' ? '30%' :
      step === 'signing' ? '60%' :
        step === 'verifying' ? '85%' :
          step === 'success' ? '100%' : '0%';

  const stepLabel =
    step === 'connecting' ? `${selectedWalletName.toUpperCase()} BAĞLANIYOR...` :
      step === 'signing' ? `${selectedWalletName.toUpperCase()} MESAJ İMZALANIYOR...` :
        step === 'verifying' ? 'KİMLİK DOĞRULANIYOR...' :
          step === 'success' ? 'ON-CHAIN OTURUM AÇILDI ✓' :
            step === 'mobile-redirect' ? `${selectedWalletName.toUpperCase()}'A YÖNLENDİRİLİYOR...` : '';

  if (!isOpen) return null;

  const isProcessing = ['connecting', 'signing', 'verifying', 'mobile-redirect'].includes(step);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="fixed inset-0 w-screen h-[100dvh] flex flex-col z-[100] bg-black/95 backdrop-blur-3xl overflow-hidden select-none"
      >
        {/* Header - Fixed Top Bar */}
        <header className="w-full h-16 pt-[env(safe-area-inset-top)] flex-none flex items-center justify-between px-6 bg-black/20 backdrop-blur-md border-b border-white/5 relative z-50">
          <div className="w-10" />

          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
            <span
              className="text-lg tracking-[0.2em] font-light"
              style={{
                fontFamily: '"Courier New", monospace',
                color: 'rgba(255,255,255,0.92)',
                textShadow: '0 0 12px rgba(255,255,255,0.15)',
              }}
            >
              MaykutoGPT
            </span>
          </div>

          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-2 rounded-full hover:bg-white/10 transition-colors text-white/60 hover:text-white disabled:opacity-30 disabled:pointer-events-none relative z-50"
          >
            <X size={24} strokeWidth={1.5} />
          </button>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 w-full overflow-y-auto flex items-center justify-center p-6 md:p-12">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 15 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="w-full max-w-xl px-6 py-12 flex flex-col justify-center min-h-[calc(100vh-4rem)] relative"
            onClick={e => e.stopPropagation()}
          >
            {/* Title */}
            <h2
              className="text-lg md:text-xl font-light tracking-[0.15em] mb-5 text-white uppercase"
              style={{ fontFamily: '"Courier New", monospace' }}
            >
              OTURUM AÇ
            </h2>

            {/* Content */}
            <div className="space-y-4 text-[14px] leading-relaxed tracking-wide text-white/70 font-light mb-5">
              <p>
                <strong className="text-white">MaykutoGPT</strong>, bir dijital zihindir. Ziyaretçi oturumlarında gerçekleştirilen tüm konuşmalar, tarayıcınızın geçici bellek katmanında tutulur.
              </p>
              <p>
                Sohbetlerinizin <strong className="text-white">Solana blokzincirinde</strong> kriptografik bir kanıt (<span className="font-mono text-[#e74c3c] text-xs">on-chain proof</span>) olarak size özel olarak saklanması ve MaykutoGPT'nin sizinle ortak "vibe"ını sonsuza dek hatırlaması için kimlik doğrulama gerekmektedir.
              </p>
              <p>
                Solana cüzdanınız ile vereceğiniz güvenli bir imza, <strong className="text-white">sohbet geçmişinizi cüzdan adresinize kriptografik olarak bağlar.</strong> Böylece zamanın ve tarayıcı sınırlarının ötesinde, geçmişteki her detayı hatırlayan size özel bir dijital zihin inşa edilir.
              </p>

              {/* Signature & Technical Details Toggle Row */}
              <div className="pt-3 flex justify-between items-center border-t border-white/5">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="text-[11px] font-mono tracking-wider text-white/30 hover:text-[#b91c1c] transition-colors duration-300 bg-transparent border-none cursor-pointer p-0 outline-none inline-flex items-center gap-1.5"
                >
                  <span>{showDetails ? "▲ TEKNİK DETAYLARI GİZLE" : "▼ TEKNİK DETAYLAR"}</span>
                </button>
                <span
                  className="text-[11px] font-light tracking-widest text-white/40"
                  style={{ fontFamily: '"Courier New", monospace' }}
                >
                  - Mehmet Aykut Öztürk
                </span>
              </div>

              {/* Expandable Details Content */}
              <AnimatePresence>
                {showDetails && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden mt-3 p-4 rounded-xl bg-black/40 border border-white/5 text-[12px] text-white/50 font-mono text-left space-y-3 leading-relaxed"
                  >
                    <p className="text-[#b91c1c] font-bold tracking-wider">
                      &gt;&gt; SISTEM MIMARISI:
                    </p>
                    <p>
                      1. <strong className="text-white">ED25519 Nonce Challenge:</strong> Oturum açma akışı, Solana ağının yerel Eliptik Eğri Kriptografisi (ED25519) kullanılarak, tarayıcı taraflı challenge nonce ile çift yönlü imza doğrulaması gerçekleştirir.
                    </p>
                    <p>
                      2. <strong className="text-white">On-Chain State Anchoring:</strong> Dijital zihin durumları ve sohbet geçmişinizin kriptografik özetleri (merkle roots), cüzdan adresinizle on-chain referanslar üzerinden merkeziyetsiz bir kanıt olarak mühürlenir.
                    </p>
                    <p>
                      3. <strong className="text-white">Cognitive Context Retrieval:</strong> Yapay zeka bağlam motoru, cüzdan adresinize bağlı bu kriptografik kanıtı okuyarak geçmiş prompt belleklerini sıfır-bilgi kanıtı (ZKP) güvenliğinde çözer ve LLM bağlamına dinamik olarak enjekte eder.
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Action Area */}
            <div className="w-full space-y-3 mb-6">
              {/* Error */}
              {step === 'error' && (
                <div
                  className="w-full py-3 px-4 rounded-xl text-[12px] font-mono tracking-wide text-center"
                  style={{
                    background: 'rgba(192,57,43,0.1)',
                    border: '1px solid rgba(192,57,43,0.3)',
                    color: '#e74c3c',
                  }}
                >
                  ⚠ {errorMsg}
                </div>
              )}

              {/* Progress bar */}
              {isProcessing && (
                <div
                  className="w-full py-4 px-6 rounded-xl flex flex-col items-center justify-center gap-2 font-mono text-[11px] tracking-wider"
                  style={{
                    background: 'rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    color: 'rgba(255, 255, 255, 0.6)',
                  }}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#7f1d1d] animate-pulse" />
                    {stepLabel}
                  </div>
                  <div className="w-full bg-white/5 h-0.5 rounded-full overflow-hidden mt-1.5">
                    <motion.div
                      initial={{ width: '0%' }}
                      animate={{ width: progressWidth }}
                      transition={{ duration: 0.8 }}
                      className="h-full"
                      style={{ background: 'linear-gradient(90deg, #7f1d1d, #b91c1c)' }}
                    />
                  </div>
                </div>
              )}

              {/* Success */}
              {step === 'success' && (
                <div
                  className="w-full py-4 px-6 rounded-xl flex items-center justify-center gap-2 font-mono text-[12px] tracking-wider"
                  style={{
                    background: 'rgba(34,197,94,0.08)',
                    border: '1px solid rgba(34,197,94,0.2)',
                    color: '#22c55e',
                  }}
                >
                  ON-CHAIN OTURUM AÇILDI ✓
                </div>
              )}

              {/* Main connect button */}
              {!isProcessing && step !== 'success' && !walletAddress && (
                <button
                  onClick={() => setShowWalletSelector(true)}
                  className="w-full py-3.5 px-6 rounded-xl flex items-center justify-center gap-2.5 font-medium text-xs tracking-[0.15em] uppercase transition-all duration-300"
                  style={{
                    background: '#7f1d1d',
                    color: '#ffffff',
                    border: '1px solid rgba(255, 255, 255, 0.05)',
                    boxShadow: '0 4px 20px rgba(127, 29, 29, 0.35)',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#b91c1c';
                    e.currentTarget.style.boxShadow = '0 6px 25px rgba(127, 29, 29, 0.5)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#7f1d1d';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(127, 29, 29, 0.35)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <LogIn size={15} strokeWidth={2} />
                  <span>SOLANA CONNECT</span>
                </button>
              )}

              {/* Disconnect (already connected) */}
              {walletAddress && !isProcessing && step !== 'success' && (
                <button
                  onClick={handleDisconnect}
                  className="w-full py-3 px-6 rounded-xl text-xs font-mono tracking-wider uppercase transition-all duration-300"
                  style={{
                    background: 'rgba(192,57,43,0.08)',
                    border: '1px solid rgba(192,57,43,0.2)',
                    color: 'rgba(192,57,43,0.8)',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(192,57,43,0.15)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'rgba(192,57,43,0.08)'}
                >
                  OTURUMU KAPAT
                </button>
              )}
            </div>
          </motion.div>
        </main>
      </motion.div>

      {showWalletSelector && (
        <WalletModal
          onClose={() => setShowWalletSelector(false)}
          onWalletSelected={(walletName) => {
            setSelectedWalletName(walletName);
            setStep('connecting');
          }}
        />
      )}
    </AnimatePresence>
  );
};

// --- Main App ---
function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  // Auth & wallet state
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('mgpt_token') || null);
  const [walletAddress, setWalletAddress] = useState(() => localStorage.getItem('mgpt_wallet') || null);

  // Tesseract State
  const [isTesseractActive, setIsTesseractActive] = useState(false);
  const [isGlitching, setIsGlitching] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [hasAskedMerveQuestion, setHasAskedMerveQuestion] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Auth success callback
  const handleAuthSuccess = useCallback((token, address) => {
    setAuthToken(token);
    setWalletAddress(address);
    if (!token) {
      setHistoryLoaded(false);
    }
  }, []);

  // Uygulama açılırken JWT varsa geçmişi yükle
  useEffect(() => {
    if (!authToken || historyLoaded) return;
    (async () => {
      try {
        const historyMsgs = await fetchHistory(authToken);
        if (historyMsgs.length > 0) {
          // MongoDB mesajlarını uygulama formatına çevir
          const converted = historyMsgs.map((m, i) => ({
            sender: m.role === 'user' ? 'user' : 'bot',
            text: m.content,
            timestamp: m.timestamp || new Date().toISOString(),
          }));
          setMessages(converted);
        }
      } catch (err) {
        console.warn('History load failed:', err);
        // Token geçersizse temizle
        if (err.message?.includes('401') || err.message?.includes('Unauthorized')) {
          localStorage.removeItem('mgpt_token');
          localStorage.removeItem('mgpt_wallet');
          setAuthToken(null);
          setWalletAddress(null);
        }
      } finally {
        setHistoryLoaded(true);
      }
    })();
  }, [authToken, historyLoaded]);
  const audioRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);

  // Smart Scroll
  const scrollToBottom = () => {
    setTimeout(() => {
      if (isAtBottomRef.current) {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight <= 50;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleSend = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!input.trim()) return;

    const userMsg = {
      sender: "user",
      text: input,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");

    const lowerInput = input.trim().toLowerCase();

    // 1. Check for "merve" or "merbe" -> Special Question (Step 1)
    if (lowerInput.includes("merve") || lowerInput.includes("merbe")) {
      setLoading(true);
      setTimeout(() => {
        const reply = "Hangi Merve? Aykut duştan çıktıktan sonra Merve ona ne derdi?";
        setMessages((prev) => [
          ...prev,
          { sender: "bot", text: reply, timestamp: new Date().toISOString() },
        ]);
        setHasAskedMerveQuestion(true);
        setLoading(false);
      }, 3000);
      return;
    }

    // 2. Check for "mavi balina" variants -> ACTIVATE TARS (Step 2)
    const isMaviBalina = /ma[vb]i\s+balina[m\u00F6]?/i.test(lowerInput);
    if (hasAskedMerveQuestion && isMaviBalina) {
      if (!audioRef.current) {
        audioRef.current = new Audio("/stay.mp3");
        audioRef.current.loop = true;
      }
      audioRef.current.play().catch((err) => console.log("Audio play failed:", err));
      setIsGlitching(true);
      setTimeout(() => {
        setIsTesseractActive(true);
        axios.post("/api/log-tesseract").catch((err) => console.error("Log failed:", err));
      }, 2000);
      return;
    }

    // Standard AI call
    setLoading(true);
    const startTime = Date.now();
    // Giriş yapılmışsa son 20 mesajı memory'ye ekle, değilse son 6
    const memory = messages.slice(authToken ? -20 : -6);
    const reply = await askAI(input, memory, "maykutogpt");

    // En az 3 saniye (3000ms) sürmesini garantile
    const elapsedTime = Date.now() - startTime;
    if (elapsedTime < 3000) {
      await new Promise((resolve) => setTimeout(resolve, 3000 - elapsedTime));
    }

    const botMsg = { sender: "bot", text: reply, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, botMsg]);
    setLoading(false);

    // Giriş yapılmışsa mesaj çiftini MongoDB'ye kaydet
    if (authToken) {
      saveHistoryPair(authToken, input, reply);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <AnimatePresence mode="wait">
      {!isTesseractActive ? (
        <motion.div
          key="standard-mode"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 1 } }}
          className="fixed inset-0 w-screen h-[100dvh] flex flex-col overflow-hidden text-white font-sans"
          style={{ background: '#050505' }}
        >
          {/* Header */}
          <div className="flex-none z-50 w-full relative">
            <GlassHeader
              isGlitching={isGlitching}
              onLoginClick={() => setIsAuthModalOpen(true)}
              walletAddress={walletAddress}
            />
          </div>

          {/* Message scroll area */}
          <motion.main
            ref={scrollContainerRef}
            onScroll={handleScroll}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 1 }}
            className="flex-1 w-full overflow-y-scroll overflow-x-hidden z-10 p-4 sm:px-6 md:px-12 scroll-smooth flex flex-col min-h-0 overscroll-y-contain touch-pan-y relative"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {/* Premium Watermark (only shown when chat is empty) */}
            {messages.length === 0 && (
              <div 
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center justify-center pointer-events-none select-none z-0"
                style={{ opacity: 0.035 }}
              >
                <span 
                  className="text-5xl sm:text-7xl md:text-8xl font-light tracking-[0.25em]"
                  style={{
                    fontFamily: '"Courier New", monospace',
                    color: '#ffffff',
                    textShadow: '0 0 25px rgba(255,255,255,0.2)',
                  }}
                >
                  MaykutoGPT
                </span>
                <span 
                  className="text-[9px] sm:text-xs font-mono tracking-[0.4em] uppercase mt-4 text-white/80"
                >
                  
                </span>
              </div>
            )}

            <div className="mt-auto flex flex-col py-2 w-full max-w-4xl mx-auto z-10">
              <AnimatePresence initial={false}>
                {messages.map((msg, i) => (
                  <div
                    key={i}
                    style={{
                      animation: isGlitching
                        ? `glitch-fade-out 2s forwards ${Math.random() * 0.5}s`
                        : 'none',
                    }}
                  >
                    <MessageBubble message={msg} />
                  </div>
                ))}
              </AnimatePresence>

              {loading && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex justify-start mb-6"
                  style={{ animation: isGlitching ? "glitch-fade-out 2s forwards" : 'none' }}
                >
                  <div
                    className="px-6 py-4"
                    style={{
                      borderRadius: '1.5rem 1.5rem 1.5rem 0.25rem',
                      background: 'rgba(0,0,0,0.6)',
                      border: '1px solid rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(20px)',
                    }}
                  >
                    <ThinkingIndicator />
                  </div>
                </motion.div>
              )}

              <div ref={messagesEndRef} className="h-0 flex-none" />
            </div>
          </motion.main>

          {/* Input Area */}
          <div
            className="flex-none z-50 w-full flex justify-center px-4 pb-4 pt-2 relative"
            style={{ background: 'linear-gradient(to top, #050505, rgba(5,5,5,0.8), transparent)' }}
          >
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8, ease: "easeOut" }}
              className="w-full max-w-4xl flex items-center p-1.5 relative"
              style={{
                background: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '9999px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                animation: isGlitching ? "glitch-fade-out 2s forwards" : undefined,
              }}
            >
              <input
                type="text"
                placeholder="Bir şey yaz..."
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 bg-transparent border-none outline-none pl-6 pr-2 font-light tracking-wide text-[15px] relative z-10"
                style={{
                  color: '#ffffff',
                  fontFamily: 'inherit',
                }}
                autoComplete="off"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim()}
                className="p-3 rounded-full flex items-center justify-center transition-all duration-300 relative z-10"
                style={{
                  color: input.trim() ? '#c0392b' : 'rgba(255,255,255,0.2)',
                  cursor: input.trim() ? 'pointer' : 'default',
                  background: input.trim() ? 'rgba(192,57,43,0.1)' : 'transparent',
                  boxShadow: input.trim() ? '0 0 15px rgba(192,57,43,0.2)' : 'none',
                }}
              >
                <Send size={22} strokeWidth={2} />
              </button>
            </motion.div>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="tesseract-mode"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
          style={{ width: '100vw', height: '100vh' }}
        >
          <TesseractMode />
        </motion.div>
      )}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onAuthSuccess={handleAuthSuccess}
        walletAddress={walletAddress}
      />
    </AnimatePresence>
  );
}

export default App;
